import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { money } from "../lib/api";
import { HardwireLogo } from "../components/HardwireMark";
import { PaymentModeBadge } from '../components/PaymentModeBadge';
import { ShieldCheck, Loader2, CheckCircle2, Lock, Phone } from "lucide-react";

export default function PayDeposit() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/pay/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setError("This payment link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/pay/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setPaying(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5 py-10">
      <div className="w-full max-w-lg">
        <div className="mb-6 flex justify-center"><HardwireLogo size={44} /></div>
        <div className="mb-4"><PaymentModeBadge /></div>
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl" data-testid="pay-card">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div>
          ) : error ? (
            <div className="p-8 text-center" data-testid="pay-error">
              <p className="text-lg font-semibold text-slate-800">{error}</p>
              <a href="tel:3526153878" className="mt-4 inline-flex items-center gap-2 text-[#1286F2] font-semibold"><Phone size={16} /> 352-615-3878</a>
            </div>
          ) : info.payment_status === "paid" ? (
            <div className="p-10 text-center" data-testid="pay-already">
              <CheckCircle2 size={56} className="mx-auto text-emerald-500" />
              <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Deposit received</h1>
              <p className="mt-2 text-slate-500">Thank you! Your {info.category} project deposit is paid. We'll be in touch to schedule.</p>
            </div>
          ) : (
            <>
              <div className="bg-[#0B1120] px-8 py-6 text-white">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#38BDF8]">Approved Estimate</div>
                <h1 className="mt-1 text-2xl font-extrabold">{info.category}</h1>
                <p className="mt-1 text-slate-300">Hi {(info.name || "there").split(" ")[0]}, your estimate is ready.</p>
              </div>
              <div className="space-y-5 p-8">
                <Row label="Estimate range" value={`${money(info.final_low)} – ${money(info.final_high)}`} />
                {info.office_note && <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">{info.office_note}</p>}
                <div className="rounded-2xl border-2 border-[#1286F2]/20 bg-blue-50/50 p-5 text-center">
                  <div className="text-xs font-bold uppercase tracking-widest text-[#1286F2]">Deposit to reserve your spot</div>
                  <div className="mt-1 text-4xl font-black text-slate-900" data-testid="deposit-amount">{money(info.deposit_amount)}</div>
                  <div className="mt-1 text-xs text-slate-500">Applied to your final invoice</div>
                </div>
                <button onClick={pay} disabled={paying} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1286F2] py-4 text-lg font-bold text-white transition hover:bg-[#0A84FF] disabled:opacity-60" data-testid="pay-now-btn">
                  {paying ? <Loader2 className="animate-spin" size={20} /> : <Lock size={18} />}
                  {paying ? "Redirecting to secure checkout…" : `Pay Deposit ${money(info.deposit_amount)}`}
                </button>
                <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
                  <ShieldCheck size={14} /> Secure payment processed by Stripe
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}
